import { EHeadingStyleVariant, Heading } from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import rentACarImage from '@/assets/home/hero/rent-a-car.jpg';
import RideLogo from '@/assets/icons/ride-logo.svg';
import { countActiveFilters } from '@/components/route/search/AlertMessage/noResultsFilters';
import Anchor from '@/components/switchback/Anchor/Anchor';
import Link from '@/components/switchback/Link/Link';
import { ESearchFilters } from '@/constants/searchFilters';
import { useRideEntryPointImpression } from '@/hooks/useRideEntryPointImpression';
import { useSectionIsInView } from '@/hooks/useSectionIsInView';
import { getQueryParams, TQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { trackPromoBannerClicked, trackPromoBannerViewed } from '@/services/analytics/promo';
import {
  ERideTransitionPlacement,
  trackRideTransitionSelectedEvent,
} from '@/services/analytics/ride';
import {
  trackRenterSearchedEvent,
  trackSrpRideCrossSellCardClicked,
  trackSrpRideCrossSellCardViewed,
} from '@/services/analytics/search';
import { ESearchFilterCategory, TFlexibleDateRange } from '@/services/analytics/search/types';
import { ERentalType } from '@/services/analytics/types';
import { buildRideSearchUrl } from '@/utility/rideSearch';
import { LocationGroupType } from '@/utility/surfacedLocation';

/** YYYY-MM-DD string to unix seconds, or null if absent/invalid. */
const toUnixSeconds = (date?: string | string[]): number | null => {
  if (!date || Array.isArray(date)) return null;
  const ms = new Date(date).getTime();
  return isNaN(ms) ? null : Math.floor(ms / 1000);
};

// Shared by the viewed and clicked promo-banner events so the pair can't drift.
const BANNER_TEXT = "Just need a car? We've got those too.";
const BANNER_CTA = 'Rent a car';
const BANNER_NAME = 'srp_ride_cross_sell';

interface IRideCrossSellTileProps {
  placement: ERideTransitionPlacement;
}

// The parent grid only mounts this once Ride supply is confirmed, so the observers attach on mount.
export const RideCrossSellTile = ({ placement }: IRideCrossSellTileProps) => {
  const intl = useIntl();
  const queryParams: TQueryParams = useSelector(getQueryParams);
  const searchMeta = useSelector(getSearchResultsMeta);
  const totalListingsFound = searchMeta.total;

  const address = queryParams[ESearchFilters.ADDRESS] as string | undefined;
  const dateFrom = queryParams[ESearchFilters.DATE_FROM] as string | undefined;
  const dateTo = queryParams[ESearchFilters.DATE_TO] as string | undefined;

  const rideUrl = buildRideSearchUrl({
    city: address,
    pickup: dateFrom,
    return: dateTo,
    utm: { medium: 'srp', campaign: 'srp_cross_sell_position_12' },
  });

  const [sectionRef, isInView] = useSectionIsInView<HTMLLIElement>('0px');
  const rideImpressionRef = useRideEntryPointImpression({ placement, destinationUrl: rideUrl });
  const setTileRef = useCallback(
    (node: HTMLLIElement | null) => {
      (sectionRef as MutableRefObject<HTMLLIElement | null>).current = node;
      rideImpressionRef(node);
    },
    [sectionRef, rideImpressionRef],
  );
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!isInView || hasTrackedView.current) return;
    hasTrackedView.current = true;

    const hasDates = Boolean(dateFrom || dateTo);
    const activeFilterCount = countActiveFilters(queryParams);

    trackSrpRideCrossSellCardViewed({
      rentalType: ERentalType.RV,
      totalListingsFound,
      searchQuery: address || '',
      hasDates,
      activeFilterCount,
    });

    trackPromoBannerViewed({
      bannerText: BANNER_TEXT,
      bannerCTA: BANNER_CTA,
      bannerLink: rideUrl,
      bannerName: BANNER_NAME,
      source: 'srp',
    });
  }, [isInView, dateFrom, dateTo, queryParams, totalListingsFound, address, rideUrl]);

  const handleClick = () => {
    trackSrpRideCrossSellCardClicked({
      rentalType: ERentalType.RV,
      totalListingsFound,
      destinationUrl: rideUrl,
      searchQuery: address || '',
    });

    trackRenterSearchedEvent({
      rentalType: ERentalType.AUTO,
      searchFilterCategory: ESearchFilterCategory.RENT_A_CAR,
      source: 'srp_ride_cross_sell_card',
      surfacedLocations: LocationGroupType.OTHER,
      locationQuery: (queryParams[ESearchFilters.ADDRESS] as string) || null,
      departureDate: toUnixSeconds(queryParams[ESearchFilters.DATE_FROM] as string | undefined),
      returnDate: toUnixSeconds(queryParams[ESearchFilters.DATE_TO] as string | undefined),
      totalAdults: queryParams[ESearchFilters.GUESTS_ADULTS]
        ? Number(queryParams[ESearchFilters.GUESTS_ADULTS])
        : null,
      totalChildren: queryParams[ESearchFilters.GUESTS_CHILDREN]
        ? Number(queryParams[ESearchFilters.GUESTS_CHILDREN])
        : null,
      totalInfants: queryParams[ESearchFilters.GUESTS_INFANTS]
        ? Number(queryParams[ESearchFilters.GUESTS_INFANTS])
        : null,
      flexibleDateRange: (queryParams[ESearchFilters.FLEXIBLE_DAYS] as TFlexibleDateRange) ?? null,
      isADAAccessible: false,
      isPetFriendly: false,
      hasCategories: false,
      selectedCategory: null,
      towableYear: null,
      towableMake: null,
      towableModel: null,
      lastQuestionSeen: null,
    });

    trackRideTransitionSelectedEvent({
      placement,
      destinationUrl: rideUrl,
    });

    trackPromoBannerClicked({
      bannerText: BANNER_TEXT,
      bannerCTA: BANNER_CTA,
      bannerLink: rideUrl,
      bannerName: BANNER_NAME,
      source: 'srp',
    });
  };

  return (
    <li
      ref={setTileRef}
      className="relative overflow-hidden group rounded-2xl"
      data-testid="ride-cross-sell-card">
      <Link href={rideUrl} passHref legacy>
        <Anchor
          href={rideUrl}
          onClick={handleClick}
          target="_blank"
          rel="noreferrer"
          className="block h-full min-h-[19rem]">
          <Image
            fill
            alt={intl.formatMessage({
              id: 'xK18Ai',
              defaultMessage: 'Rent a car on Ride – An Outdoorsy Company',
              description: 'SRP > Ride cross-sell card > image alt text',
            })}
            src={rentACarImage.src}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-700 ease-in-out lg:group-hover:scale-110"
          />

          {/* Constant scrim so text stays readable on the bright car photo */}
          <div className="absolute inset-0 bg-black/25" />

          {/* Gradient overlay */}
          <div className="absolute inset-0 opacity-50 bg-gradient-to-b from-transparent to-[rgba(0,0,0,0.4)] transition-opacity duration-700 ease-in-out group-hover:opacity-100" />

          <div className="absolute bottom-0 left-0 right-0 flex flex-col px-8 py-6 text-white gap-6">
            {/* font-size:32px font-weight:400 line-height:100% — matches HeroCard .title */}
            <Heading
              component="div"
              variant={EHeadingStyleVariant.H4Alt}
              className="font-normal leading-none text-[32px]">
              {intl.formatMessage(
                {
                  id: 'tH9QeJ',
                  defaultMessage: "Just need a car?{br}We've got those too.",
                  description: 'SRP > Ride cross-sell card > title',
                },
                { br: <br /> },
              )}
            </Heading>

            <div className="flex items-center self-start gap-3">
              {/* Styled as a button but rendered as a span — the parent <a> is the interactive element */}
              <span className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-white rounded-full pointer-events-none shrink-0">
                {intl.formatMessage({
                  id: 'w4ve6+',
                  defaultMessage: 'Rent a car',
                  description: 'SRP > Ride cross-sell card > CTA',
                })}
              </span>

              {/* Brand attribution — hidden on mobile to keep the CTA on one line */}
              <span className="flex-col items-start hidden gap-0.5 lg:flex">
                <RideLogo role="img" aria-label="Ride" className="w-auto h-[18px]" />
                <span className="text-xs whitespace-nowrap">
                  {intl.formatMessage({
                    id: 'qIj89f',
                    defaultMessage: 'An Outdoorsy Company',
                    description: 'SRP > Ride cross-sell card > Ride brand attribution',
                  })}
                </span>
              </span>
            </div>
          </div>
        </Anchor>
      </Link>
    </li>
  );
};
